import { supabase } from '@/integrations/supabase/client';
import {
  toInfluencerProfileUpdate,
  validateInfluencerHandles,
  type ValidatedInfluencerProfile,
} from '@/lib/influencerValidation';
import { BATCH_SIZE, BATCH_DELAY_MS } from '../constants';
import { chunk, sleep, normalizeHandle } from './refreshHelpers';

export interface DbInfluencer {
  id: string;
  handle: string;
  instagram_pk: string | number | null;
}

export interface ProfileFetchResult {
  profilesByInfluencerId: Map<string, ValidatedInfluencerProfile>;
  failedHandles: string[];
  failureReasons: string[];
}

export interface ProfilePersistResult {
  succeededIds: string[];
  metricReadyInfluencerIds: Set<string>;
  failedHandles: string[];
  failureReasons: string[];
}

/**
 * Validates and fetches influencer profile data in chunks of BATCH_SIZE.
 */
export async function validateAndFetchProfilesInBatches(
  dbInfluencers: DbInfluencer[],
  onBatchProcessed: (processedCount: number) => void,
  onBatchComplete: () => void,
): Promise<ProfileFetchResult> {
  const batches = chunk(dbInfluencers, BATCH_SIZE);
  const failedHandles: string[] = [];
  const failureReasons: string[] = [];
  const failureReasonByHandle = new Map<string, string>();
  const profilesByInfluencerId = new Map<string, ValidatedInfluencerProfile>();
  let processedSoFar = 0;

  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    const batchHandles = batch.map((inf) => inf.handle);

    // Delay between batches to avoid HikerAPI rate-limiting
    if (batchIndex > 0) {
      await sleep(BATCH_DELAY_MS);
    }

    try {
      // Step 1: Call validate-influencer-handles for this batch
      const profileResult = await validateInfluencerHandles(batchHandles, {
        includeMedia: false,
      });

      // Track invalid handles but don't abort — continue with valid ones
      if (profileResult.invalidHandles.length > 0) {
        failedHandles.push(...profileResult.invalidHandles);
        for (const invalidHandle of profileResult.invalidHandles) {
          const normalized = normalizeHandle(invalidHandle);
          const invalidReason =
            profileResult.invalidReasons[normalized] ??
            profileResult.invalidReasons[invalidHandle] ??
            `@${invalidHandle} couldn't be refreshed right now. Please try again later.`;

          if (!failureReasonByHandle.has(normalized)) {
            failureReasonByHandle.set(normalized, invalidReason);
            failureReasons.push(invalidReason);
          }
        }
      }

      // Map returned profiles by normalized handle for quick lookup
      const profileByHandle = new Map(
        profileResult.profiles.map((profile) => [
          normalizeHandle(profile.handle),
          profile,
        ]),
      );

      for (const influencer of batch) {
        const profile = profileByHandle.get(normalizeHandle(influencer.handle));
        if (!profile) {
          if (!failedHandles.includes(influencer.handle)) {
            failedHandles.push(influencer.handle);
          }
          const normalized = normalizeHandle(influencer.handle);
          if (!failureReasonByHandle.has(normalized)) {
            const fallbackReason = `@${influencer.handle} did not return updated profile details.`;
            failureReasonByHandle.set(normalized, fallbackReason);
            failureReasons.push(fallbackReason);
          }
          continue;
        }

        profilesByInfluencerId.set(influencer.id, profile);
      }
    } catch (batchErr: unknown) {
      console.error('Batch validation failed:', batchErr);
      failedHandles.push(...batchHandles);
      failureReasons.push(
        batchErr instanceof Error ? batchErr.message : String(batchErr),
      );
    }

    processedSoFar += batch.length;
    onBatchProcessed(processedSoFar);
    onBatchComplete();
  }

  return { profilesByInfluencerId, failedHandles, failureReasons };
}

/**
 * Persists all freshly validated influencer profiles to Supabase.
 */
export async function persistRefreshedProfiles(
  dbInfluencers: DbInfluencer[],
  profilesByInfluencerId: Map<string, ValidatedInfluencerProfile>,
  initialFailedHandles: string[],
  initialFailureReasons: string[],
): Promise<ProfilePersistResult> {
  const succeededIds: string[] = [];
  const metricReadyInfluencerIds = new Set<string>();
  const failedHandles = [...initialFailedHandles];
  const failureReasons = [...initialFailureReasons];

  for (const influencer of dbInfluencers) {
    if (influencer.instagram_pk !== null && influencer.instagram_pk !== undefined) {
      metricReadyInfluencerIds.add(influencer.id);
    }

    const profile = profilesByInfluencerId.get(influencer.id);
    if (!profile) continue;

    try {
      const { error: updateError } = await supabase
        .from('influencers')
        .update(toInfluencerProfileUpdate(profile, { preserveMetrics: true }))
        .eq('id', influencer.id);

      if (updateError) {
        console.error(`DB update failed for @${influencer.handle}:`, updateError.message);
        failedHandles.push(influencer.handle);
        failureReasons.push(`@${influencer.handle}: ${updateError.message}`);
        continue;
      }

      succeededIds.push(influencer.id);
      metricReadyInfluencerIds.add(influencer.id);
    } catch (updateErr: unknown) {
      console.error(`DB update error for @${influencer.handle}:`, updateErr);
      failedHandles.push(influencer.handle);
      failureReasons.push(
        updateErr instanceof Error ? updateErr.message : String(updateErr),
      );
    }
  }

  return {
    succeededIds,
    metricReadyInfluencerIds,
    failedHandles,
    failureReasons,
  };
}

/**
 * Enqueues background metrics enrichment for all influencers with confirmed Instagram IDs.
 */
export async function enqueueMetricsBatches(
  metricTargets: Array<{ id: string; handle: string }>,
  enqueueJob: (ids: string[], handles: string[]) => Promise<unknown>,
): Promise<void> {
  if (metricTargets.length === 0) return;

  const metricBatches = chunk(metricTargets, BATCH_SIZE);

  for (let i = 0; i < metricBatches.length; i++) {
    const metricBatch = metricBatches[i];

    if (i > 0) {
      await sleep(BATCH_DELAY_MS);
    }

    try {
      await enqueueJob(
        metricBatch.map((inf) => inf.id),
        metricBatch.map((inf) => inf.handle),
      );
    } catch (metricsErr: unknown) {
      console.error('Metrics enrichment batch failed:', metricsErr);
    }
  }
}
